import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { getCurrentUser } from '../../store/slices/authSlice';
import { buildLoginPathForReturn } from '../../lib/authReturnContext';
import ProtectedRouteBootstrapContinuity, {
  isChunk332ProtectedBootstrapPath,
} from './ProtectedRouteBootstrapContinuity';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const hasToken = Boolean(localStorage.getItem('accessToken'));
  const [isBootstrapping, setIsBootstrapping] = React.useState(
    hasToken && !isAuthenticated
  );
  const shouldHandOffBootstrap = isChunk332ProtectedBootstrapPath(location.pathname);

  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      if (!hasToken || isAuthenticated) {
        if (isMounted) {
          setIsBootstrapping(false);
        }
        return;
      }

      try {
        await dispatch(getCurrentUser()).unwrap();
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        if (isMounted) {
          setIsBootstrapping(false);
        }
      }
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [hasToken, isAuthenticated, dispatch]);

  if (isLoading || isBootstrapping) {
    if (shouldHandOffBootstrap) {
      return <ProtectedRouteBootstrapContinuity pathname={location.pathname} />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={buildLoginPathForReturn({
          pathname: location.pathname,
          search: location.search,
        })}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;